/**
 * LaGift — product gallery
 * ---------------------------------------------------------------------------
 * The stage and its thumbnails. Every frame is in the page already, stacked in
 * one grid area, so showing one is a class change: no request, and no layout
 * jump when a thumbnail is pressed.
 *
 * It deliberately does not listen for `lagift:variant:change` itself. On the
 * product page the picker is in the information column and the gallery is in
 * the media column, so the event never passes through here - it bubbles past
 * both to whatever wraps them. That element owns the coordination and calls
 * `showMedia()`; this element owns the gallery.
 */
/**
 * Scoped so the file can run more than once. A `<script src>` executes once per
 * tag, not once per URL, and a top-level class throws "already been declared"
 * on the second run. See CLAUDE.md.
 */
(() => {
  class LagiftProductGallery extends HTMLElement {
    connectedCallback() {
      this.frames = [...this.querySelectorAll('[data-gallery-media]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.galleryThumb));
      });
    }

    /**
     * Bring one medium forward. Public: the section element calls this when a
     * variant with its own photograph is chosen.
     *
     * An id this gallery does not hold is ignored rather than clearing the
     * stage. A gallery rendered with a `limit` legitimately may not contain the
     * variant's image, and going blank is worse than staying where it was.
     */
    /**
     * True when the layout shows every photograph at once (stacked, or two
     * across) rather than one at a time.
     *
     * Read from the rendered result rather than from the class, because the
     * layout is chosen per breakpoint: the same gallery is one-at-a-time on a
     * phone and all-at-once on a desktop, and only the browser knows which is
     * in force right now.
     */
    get showsEveryFrame() {
      return this.frames.filter((frame) => getComputedStyle(frame).visibility === 'visible').length > 1;
    }

    showMedia(id) {
      const target = this.frames.find((frame) => frame.dataset.galleryMedia === String(id));
      if (!target) return;

      // Selecting is meaningless when they are all on screen already, so the
      // useful thing to do with a variant's photograph is go to it.
      if (this.showsEveryFrame) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }

      this.frames.forEach((frame) => frame.classList.toggle('is-current', frame === target));
      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', thumb.dataset.galleryThumb === String(id) ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('lagift-product-gallery')) {
    customElements.define('lagift-product-gallery', LagiftProductGallery);
  }
})();
